<script setup lang="ts">

import { computed } from "vue";
import { FeatureFrame } from "../Components";
import { useAppConfiguration } from "@domain/AppConfiguration";
import { useCarePlanApi } from "@app/MyGarage/Types/Api/CarePlanApi";
import { useViewModelInstance } from "@app/MyGarage/Types/MyGarageViewModelStore";
import type { ICarePlan } from "@domain/CarePlan";
import * as Icons from "../Components/ProductIcons";

import ConciergeButton from "@app/Components/Buttons/ConciergeButton.vue";
import { WorkOrderType } from "@domain/CarePlan/WorkOrders/WorkOrderType";

const app = useAppConfiguration();
const model = defineModel<ICarePlan>( { required: true } ); 
const viewModel = await useViewModelInstance();

const tyreRepair = computed( () => model.value.carbucksRewards.workOrderConfigurations.find( x => x.type == WorkOrderType.TyreRepair ) );
const windscreenRepair = computed( () => model.value.carbucksRewards.workOrderConfigurations.find( x => x.type == WorkOrderType.WindscreenRepair ) );
const healthChecks = computed( () => model.value.carbucksRewards.workOrderConfigurations.find( x => x.type == WorkOrderType.VehicleHealthChecks ) );

// TODO: This api call throws a backend exception so commenting out until we actually have Rewards T&Cs.
// let documents = null as any;
// if( model.value.carbucksRewards.workOrderConfigurations.length  > 0 )
// {
//     const api = useCarePlanApi();
//     documents = await api.getCarbucksRewardsDocumentUrls( viewModel.vehicle.vehicleId );
// }
// const termsAndConditionsLinkUrl = computed( () => documents?.termsAndConditionsLinkUrl );
const termsAndConditionsLinkUrl = null as any;

</script>

<template>

    <FeatureFrame :featured="true">
        
        <template #title>
            <div class="w-full px-8 2xl:mx-10 my-2">
                <img src="@/assets/images/rewards-logo.png" class="w-full"/>
            </div>
        </template>

        <template #sub-title>
            <div class="px-2 3xl:px-4 text-base text-center -mb-2">
                A rewards program from your dealership to help you keep your car healthy and your wallet happy
            </div>
        </template>

        <template #features>
            <div class="flex flex-wrap justify-center tracking-wider gap-y-3
                        text-xs lg:text-sm 3xl:text-xl pb-2">

                <h1 class="basis-full text-center uppercase my-3 opacity-80 text-white/60 text-xs 3xl:text-lg">
                    Included with your REWARDS program
                </h1>

                <div class="basis-1/3 flex flex-col items-center justify-center aspect-square text-center uppercase">
                    <Icons.App class="p-1 text-primary-500 opacity-80 mb-2 w-full 3xl:scale-150 3xl:mb-5" />
                    <h1>Carbucks<br/>App</h1>
                </div>
                <div class="basis-1/3 flex flex-col items-center justify-center aspect-square text-center uppercase">
                    <Icons.LiveReminders class="p-1 text-primary-500 opacity-80 mb-2 w-full 3xl:scale-150 3xl:mb-5" />
                    <h1>Live<br/>Reminders</h1>
                </div>
                <div class="basis-1/3 flex flex-col items-center justify-center aspect-square text-center uppercase">
                    <Icons.Concierge class="p-1 text-primary-500 opacity-80 mb-2 w-full 3xl:scale-150 3xl:mb-5" />
                    <h1>Concierge<br/>Service</h1>
                </div>
                <div v-if="tyreRepair != null"
                     class="basis-1/3 flex flex-col items-center justify-center aspect-square text-center uppercase">
                    <Icons.PunctureRepair class="p-1 text-primary-500 opacity-80 mb-2 w-full 3xl:scale-150 3xl:mb-5" />
                    <h1>Puncture<br/>Repairs</h1>
                </div>
                <div v-if="windscreenRepair != null"
                     class="basis-1/3 flex flex-col items-center justify-center aspect-square text-center uppercase">
                    <Icons.WindscreenRepair class="p-1 text-primary-500 opacity-80 mb-2 w-full 3xl:scale-150 3xl:mb-5" />
                    <h1>Chip<br/>Repairs</h1>
                </div>
                <div v-if="healthChecks != null"
                     class="basis-1/3 flex flex-col items-center justify-center aspect-square text-center uppercase">
                    <Icons.HealthChecks class="p-1 text-primary-500 opacity-80 mb-2 w-full 3xl:scale-150 3xl:mb-5" />
                    <h1>Health<br/>Checks</h1>
                </div>
                <div class="basis-1/3 flex flex-col items-center justify-center aspect-square text-center uppercase">
                    <Icons.Marketplace class="p-1 text-primary-500 opacity-80 mb-2 w-full 3xl:scale-150 3xl:mb-5" />
                    <h1 >Market<br/>-place</h1>
                </div>
                <div v-if="model.carbucksRewards.isCashbackEnabled"
                     class="basis-1/3 flex flex-col items-center justify-center aspect-square text-center uppercase">
                    <Icons.Cashback class="p-1 text-primary-500 opacity-80 mb-2 w-full 3xl:scale-150 3xl:mb-5" />
                    <h1>Cashback<br/>Rewards</h1>
                </div>
            </div>
        </template>

        <div class="flex flex-col w-full">

            <!-- PROPERTIES -->
            <div class="flex flex-col w-full gap-1 pt-4
                        text-xs lg:text-sm 3xl:text-base tracking-wider">
               
                <div class="flex flex-col gap-0.5 3xl:text-lg">
                    <div class="text-xs 3xl:!text-base 3xl:py-2 text-emerald-500 uppercase mb-2 text-center">
                        {{ model.carbucksRewards.title }} provided to you by<br/>{{ app.name }}
                    </div>

                    <!-- CARBUCKS APP -->
                    <div class="flex items-start gap-2 3xl:py-1">
                        <div>{{ model.carbucksRewards.carbucksAppTitle}}</div>
                        <div class="flex-1 border-b-2 border-dotted opacity-60 h-[1rem]" />
                        <Icon name="check" class="-mb-1.5 text-2xl 3xl:text-3xl" />
                    </div>  

                    <!-- TYRE PUNCTURE REPAIR -->
                    <div v-if="tyreRepair != null"
                         class="flex items-start gap-2 3xl:py-1">
                        <div>{{ tyreRepair.title }}</div>
                        <div class="flex-1 border-b-2 border-dotted opacity-60 h-[1rem]" />
                        <Icon name="check" class="-mb-1.5 text-2xl 3xl:text-3xl" />
                    </div>  

                    <!-- CHIPPED WINDSCREEN REPAIR -->
                    <div v-if="windscreenRepair != null"
                         class="flex items-start gap-2 3xl:py-1">
                        <div>{{ windscreenRepair.title }}</div>
                        <div class="flex-1 border-b-2 border-dotted opacity-60 h-[1rem]" />
                        <Icon name="check" class="-mb-1.5 text-2xl 3xl:text-3xl" />
                    </div>  

                    <!-- HEALTH & SAFETY REMINDERS -->
                    <div class="flex items-start gap-2 3xl:py-1">
                        <div>{{ model.carbucksRewards.remindersTitle}}</div>
                        <div class="flex-1 border-b-2 border-dotted opacity-60 h-[1rem]" />
                        <Icon name="check" class="-mb-1.5 text-2xl 3xl:text-3xl" />
                    </div>  

                    <!-- CONCIERGE -->
                    <div v-if="model.carbucksRewards.isConciergeEnabled" class="flex items-start gap-2 3xl:py-1">
                        <div>{{ model.carbucksRewards.conciergeTitle }}</div>
                        <div class="flex-1 border-b-2 border-dotted opacity-60 h-[1rem]" />
                        <Icon name="check" class="-mb-1.5 text-2xl 3xl:text-3xl" />
                    </div>  

                    <!-- MARKETPLACE -->
                    <div v-if="model.marketplace.isEnabled"
                         class="flex items-start gap-2 3xl:py-1">
                        <div>{{ model.marketplace.title }}</div>
                        <div class="flex-1 border-b-2 border-dotted opacity-60 h-[1rem]" />
                        <Icon name="check" class="-mb-1.5 text-2xl 3xl:text-3xl" />
                    </div>  

                    <!-- CASHBACK -->
                    <div v-if="model.carbucksRewards.isCashbackEnabled" class="flex items-start gap-2 3xl:py-1">
                        <div>{{ model.carbucksRewards.cashbackTitle }}</div>
                        <div class="flex-1 border-b-2 border-dotted opacity-60 h-[1rem]" />
                        <Icon name="check" class="-mb-1.5 text-2xl 3xl:text-3xl" />
                    </div>  
                    
                </div>
                
            </div>

            <!-- ACTIONS -->
            <div class="w-full flex flex-col
                        gap-x-1 gap-y-3 md:gap-x-2 md:gap-y-4 3xl:gap-4 mt-4 3xl:mt-6">
                
                <ConciergeButton v-if="model.carbucksRewards.isConciergeEnabled" />
                
                <JupiterButton 
                    v-if="termsAndConditionsLinkUrl"  
                    variant="outlined"
                    color="white/50"
                    type="url" 
                    :to="termsAndConditionsLinkUrl"
                    class="col-span-2 !h-10 lg:!h-12 3xl:!h-14 mt-2">
                    <span class="flex items-center gap-1 text-white">
                        <Icon name="contract" class="text-2xl !hidden md:!block" />
                        <span class="3xl:text-base">
                            Terms &amp; Conditions
                        </span>
                    </span>
                </JupiterButton>

                <div class="-mb-2 -mt-2 flex justify-stretch w-full">
                    <Button variant="text"
                            to="/my-garage/dealership"
                            class="!h-10 lg:!h-12 3xl:!h-14 grow">
                        Contact Dealer
                    </Button>
                </div>

            </div>

        </div>

   </FeatureFrame>
        
</template>
